import { z } from 'zod';

import { passesNumeralGate } from './brief-counts.ts';
import { DomainError } from './errors.ts';
import {
  type MaterialsIntake,
  type MaterialsShape,
  materialsShapeSchema,
} from './materials-shape.ts';
import { AUDIENCE_AUTHORED_TEMPLATES } from './study-type-templates/audience.ts';
import { BRAND_AUTHORED_TEMPLATES } from './study-type-templates/brand.ts';
import { CREATIVE_AUTHORED_TEMPLATES } from './study-type-templates/creative.ts';
import { PRODUCT_AUTHORED_TEMPLATES } from './study-type-templates/product.ts';

/**
 * The Study Type catalogue: four Categories, sixteen named Study Types, and Custom.
 *
 * It is code rather than rows because it is a product asset that no user extends. Rows would need
 * a seed, a migration per wording change and a read on every render, and the SPA could not draw
 * the picker without a fetch. Held here, both halves of the product bundle the same list and the
 * ids the database stores are the ids the screen shows.
 *
 * The table below is a `const` and the exports are the result of **parsing** it, not of asserting
 * it: a hand-edited entry that the schema could not take fails when this module loads, rather
 * than reaching a study. The parse is also what holds the two halves of the catalogue together —
 * every Study Type's `categoryId` must name a Category that exists, and no id may appear twice.
 *
 * The ids are a one-way door. A study stores one, so renaming an id is a data migration and not
 * an edit to this file. Names and bring/get lines are display copy and may be reworded freely.
 */
const CATEGORY_TABLE = [
  {
    id: 'brand',
    name: 'Brand',
    businessQuestion: 'How is our brand doing?',
  },
  {
    id: 'creative',
    name: 'Creative and messaging',
    businessQuestion: 'Will this work?',
  },
  {
    id: 'product',
    name: 'Product and offer',
    businessQuestion: 'Should we build or sell this?',
  },
  {
    id: 'audience',
    name: 'Audience and moment',
    businessQuestion: 'Who are we talking to, and when?',
  },
] as const;

const STUDY_TYPE_TABLE = [
  {
    id: 'brand-health',
    categoryId: 'brand',
    name: 'Brand health',
    bringGet:
      'Bring your brand and up to five competitors. Get where you stand, and why people choose or skip you.',
  },
  {
    id: 'brand-comparison',
    categoryId: 'brand',
    name: 'Brand comparison',
    bringGet: 'Bring the brands to compare. Get where you win and lose against each.',
  },
  {
    id: 'competitive-read',
    categoryId: 'brand',
    name: 'Competitive read',
    bringGet:
      'Bring the alternatives people consider. Get how they see them and where you can win.',
  },
  {
    id: 'positioning-check',
    categoryId: 'brand',
    name: 'Positioning check',
    bringGet: 'Bring the new positioning. Get whether it lands, and against whom.',
  },
  {
    id: 'naming-identity',
    categoryId: 'brand',
    name: 'Naming and identity feedback',
    bringGet: 'Bring the name, tagline, logo, or pack. Get the reaction and the reasons.',
  },
  {
    id: 'campaign-validation',
    categoryId: 'creative',
    name: 'Campaign validation',
    bringGet:
      'Bring the campaign creative and the message it should land. Get a verdict on whether it lands, with who, and what to fix.',
  },
  {
    id: 'ad-comparison',
    categoryId: 'creative',
    name: 'Ad or concept comparison',
    bringGet: 'Bring two or more versions. Get a winner and the reasons.',
  },
  {
    id: 'message-testing',
    categoryId: 'creative',
    name: 'Message testing',
    bringGet:
      'Bring the headlines, claims, or value props. Get which ones people repeat back and prefer.',
  },
  {
    id: 'creative-recall',
    categoryId: 'creative',
    name: 'Creative recall and comprehension',
    bringGet: 'Bring the finished creative. Get what people remember and what confuses them.',
  },
  {
    id: 'concept-feedback',
    categoryId: 'product',
    name: 'Concept feedback',
    bringGet: 'Bring the concept. Get whether people want it, and what would make them.',
  },
  {
    id: 'feature-priorities',
    categoryId: 'product',
    name: 'Feature and experience priorities',
    bringGet:
      'Bring the product or program. Get what people would use, what breaks today, and what to build first.',
  },
  {
    id: 'offer-pricing',
    categoryId: 'product',
    name: 'Offer and pricing reaction',
    bringGet:
      'Bring the bundles, tiers, or price points. Get the stated reaction; not a pricing study.',
  },
  {
    id: 'audience-understanding',
    categoryId: 'audience',
    name: 'Audience understanding',
    bringGet: 'Bring who you think they are. Get what they believe, want, and need to hear.',
  },
  {
    id: 'launch-planning',
    categoryId: 'audience',
    name: 'Launch planning',
    bringGet: 'Bring the launch plan. Get the objections and missing proof points.',
  },
  {
    id: 'segment-check',
    categoryId: 'audience',
    name: 'Segment or persona check',
    bringGet:
      'Bring the persona or the target groups to check. Get whether the people you mean recognise themselves in each, and what they say.',
  },
  {
    id: 'post-launch-read',
    categoryId: 'audience',
    name: 'Post-launch or campaign read',
    bringGet: 'Bring what ran. Get what people say about it now, and what to fix.',
  },
] as const;

/**
 * Freezes a parsed value and everything under it, in place.
 *
 * The immutability the registry promises is about time rather than about types: a study resolves
 * the version it stores for as long as it exists, and a consumer that reached into a template and
 * changed a line would move that version for every later resolution in the process. `readonly`
 * says so to the compiler and stops nothing at runtime, and the SPA bundles this package, so the
 * one place to hold it is where the value is built.
 */
const deepFreeze = <T>(value: T): T => {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  Object.freeze(value);
  for (const entry of Object.values(value)) {
    deepFreeze(entry);
  }

  return value;
};

/** The id a study stores when the researcher wanted none of the sixteen. */
export const CUSTOM_STUDY_TYPE_ID = 'custom';

/** What Custom is called wherever a Study Type's name is shown. */
export const CUSTOM_STUDY_TYPE_NAME = 'Custom';

/**
 * Custom's own bring/get line.
 *
 * Custom has no shape to promise, so the line says what the study will do instead of what it
 * needs: it is the one entry whose intake is a question rather than a list.
 */
export const CUSTOM_STUDY_TYPE_BRING_GET =
  'Bring whatever you have. Sigora asks what it is and offers general goal options.';

export type CategoryId = (typeof CATEGORY_TABLE)[number]['id'];

/** A named Study Type's id, or `custom`. */
export type StudyTypeId = (typeof STUDY_TYPE_TABLE)[number]['id'] | typeof CUSTOM_STUDY_TYPE_ID;

/** The four Category ids, in the order the picker shows them. */
export const CATEGORY_IDS: readonly CategoryId[] = deepFreeze(
  CATEGORY_TABLE.map((category) => category.id),
);

/** The sixteen named ids and `custom`: everything the stored column may hold. */
export const STUDY_TYPE_IDS: readonly StudyTypeId[] = deepFreeze([
  ...STUDY_TYPE_TABLE.map((studyType) => studyType.id),
  CUSTOM_STUDY_TYPE_ID,
]);

export const categoryIdSchema = z.enum(CATEGORY_IDS);
export const studyTypeIdSchema = z.enum(STUDY_TYPE_IDS);

export const studyTypeCategorySchema = z
  .object({
    id: categoryIdSchema,
    /** What the Category is called on its card. */
    name: z.string().min(1),
    /** The question the Category answers, shown under its name. */
    businessQuestion: z.string().min(1),
  })
  .readonly();
export type StudyTypeCategory = z.infer<typeof studyTypeCategorySchema>;

export const studyTypeSchema = z
  .object({
    id: studyTypeIdSchema,
    categoryId: categoryIdSchema,
    /** What the Study Type is called on its card and in the Study so far sheet. */
    name: z.string().min(1),
    /** What the researcher brings and what they get back, in one sentence each. */
    bringGet: z.string().min(1),
  })
  .readonly();
export type StudyType = z.infer<typeof studyTypeSchema>;

/**
 * Refuses a list that names the same id twice.
 *
 * Neither the enum nor the row count catches one: `z.enum` is happy to see a value twice, and the
 * length is a count of rows rather than of distinct ids. What a repeat produces is a listing that
 * draws two cards and a lookup that answers with whichever row came last, which is a picker whose
 * two choices resolve to one thing.
 */
const refuseRepeatedIds = (entries: readonly { id: string }[], context: z.RefinementCtx): void => {
  const seen = new Set<string>();

  for (const entry of entries) {
    if (seen.has(entry.id)) {
      context.addIssue({ code: 'custom', message: `duplicate id ${entry.id}` });
    }

    seen.add(entry.id);
  }
};

/**
 * The four Categories.
 *
 * Ordered by the array itself rather than by an index field, the way the step registry is: a
 * number beside each entry would be a second statement of the same fact, free to drift.
 */
export const studyTypeCategoriesSchema = z
  .array(studyTypeCategorySchema)
  .length(CATEGORY_TABLE.length)
  .superRefine(refuseRepeatedIds);

export const CATEGORIES: readonly StudyTypeCategory[] = deepFreeze(
  studyTypeCategoriesSchema.parse(CATEGORY_TABLE),
);

/**
 * The sixteen named Study Types, grouped by Category in the order the picker shows them.
 *
 * Custom is not here. It has a Category only when a researcher reaches it from one, so it is a
 * stored fact about a study rather than a row in the catalogue, and giving it one here would
 * force every consumer to filter it back out of the Category listings.
 */
export const studyTypesSchema = z
  .array(studyTypeSchema)
  .length(STUDY_TYPE_TABLE.length)
  .superRefine((studyTypes, context) => {
    refuseRepeatedIds(studyTypes, context);

    for (const studyType of studyTypes) {
      // `studyTypeIdSchema` is the stored column's alphabet and holds `custom`, which is a study's
      // answer rather than a catalogue entry. A row carrying it would reach the Category listings
      // and the registry seed and give Custom a Category the catalogue has no business deciding.
      if (studyType.id === CUSTOM_STUDY_TYPE_ID) {
        context.addIssue({
          code: 'custom',
          message: `${CUSTOM_STUDY_TYPE_ID} is not a Study Type`,
        });
      }
    }
  });

export const STUDY_TYPES: readonly StudyType[] = deepFreeze(
  studyTypesSchema.parse(STUDY_TYPE_TABLE),
);

const CATEGORY_BY_ID = new Map(CATEGORIES.map((category) => [category.id, category]));
const STUDY_TYPE_BY_ID = new Map(STUDY_TYPES.map((studyType) => [studyType.id, studyType]));

export const isCategoryId = (value: unknown): value is CategoryId =>
  typeof value === 'string' && CATEGORY_BY_ID.has(value as CategoryId);

export const isStudyTypeId = (value: unknown): value is StudyTypeId =>
  typeof value === 'string' &&
  (value === CUSTOM_STUDY_TYPE_ID || STUDY_TYPE_BY_ID.has(value as StudyTypeId));

export const categoryById = (id: CategoryId): StudyTypeCategory | undefined =>
  CATEGORY_BY_ID.get(id);

/** The named Study Type, or `undefined` for `custom`, which has no catalogue entry. */
export const studyTypeById = (id: StudyTypeId): StudyType | undefined => STUDY_TYPE_BY_ID.get(id);

/**
 * What to call a Study Type, including Custom, which `studyTypeById` has no entry for, or
 * `undefined` where the catalogue holds no such id.
 *
 * Echoing the id back would put `pricing-study` on a card as though it were display copy. An id
 * the catalogue does not hold is the same unresolvable study `templateFor` and
 * `currentTemplateVersion` answer `undefined` for, and it gets the same answer here, so a caller
 * has one rule to follow rather than three of different shapes.
 */
export const studyTypeName = (id: StudyTypeId): string | undefined =>
  id === CUSTOM_STUDY_TYPE_ID ? CUSTOM_STUDY_TYPE_NAME : STUDY_TYPE_BY_ID.get(id)?.name;

export const studyTypesInCategory = (categoryId: CategoryId): readonly StudyType[] =>
  STUDY_TYPES.filter((studyType) => studyType.categoryId === categoryId);

/**
 * A Study Type in one line, as a model is told it: its name, then what the researcher brings and
 * gets back, the same words its card shows.
 */
export const studyTypeLine = (studyType: StudyType): string =>
  `${studyType.name}: ${studyType.bringGet}`;

/**
 * The shape a drafted guide takes: an opening section and a closing one when set, and between them
 * one section per research question, in order, each holding between `questionsPerRq.min` and
 * `questionsPerRq.max` questions, with the whole guide at most `maxMinutes` long by `guideMinutes`.
 */
export const guideShapeSchema = z
  .strictObject({
    opening: z.boolean(),
    closing: z.boolean(),
    questionsPerRq: z
      .strictObject({ min: z.int().positive(), max: z.int().positive() })
      .refine((range) => range.min <= range.max, 'min must not exceed max'),
    maxMinutes: z.int().positive(),
  })
  .readonly();
export type GuideShape = z.infer<typeof guideShapeSchema>;

/** How many people a Study Type recommends a study hears from, and the sentence saying why. */
export const audienceRecommendationSchema = z
  .strictObject({
    recommendedCompletes: z.int().positive(),
    rationale: z.string().min(1),
  })
  .readonly();
export type AudienceRecommendation = z.infer<typeof audienceRecommendationSchema>;

export const studyTypeTemplateSchema = z
  .object({
    /** 1-based, and the number a study stores so it can resolve this exact content again. */
    version: z.int().positive(),
    /**
     * Whether this version was written for its Study Type or seeded from the generic one.
     *
     * It is a fact about the content, so it sits on the version rather than on the type: a type
     * whose authored version exists still has its unauthored version 1, and the studies pinned to
     * that version still resolve it.
     */
    authored: z.boolean(),
    /** What to ask for at Materials, and what to do with whatever arrives. */
    intakeShape: z.string().min(1),
    /** The facts to take out of the materials, one per line. */
    extractionSet: z.array(z.string().min(1)).min(1).readonly(),
    /** How to turn those facts into goal options, and which to recommend. */
    goalGuidance: z.string().min(1),
    /** How many research questions the type wants and what each must carry. */
    rqShape: z.string().min(1),
    /** The guide's sections, in order. */
    guideSkeleton: z.array(z.string().min(1)).min(1).readonly(),
    /** How big the audience should be and what to screen on. */
    audienceGuidance: z.string().min(1),
    /** How the Brief frames its verdict and its evidence. */
    briefShape: z.string().min(1),
    /** Any sentence the type adds to the Brief's Method block. */
    methodText: z.string().min(1),
    /**
     * The intake the Materials step shows and the facts it takes out of what arrives, as structure
     * rather than prose. Every version 1 holds none, and a study pinned to a version without one
     * has no Materials shape (`materialsShapeFor`).
     */
    materials: materialsShapeSchema.optional(),
    /**
     * How many research questions the type intends a study to start with, as a number rather than
     * prose: the drafter asks for exactly this many and refuses an answer with any other count.
     * Versions before 3 hold none, and `intendedRqCount` answers the default for them.
     */
    rqCount: z.int().positive().optional(),
    /**
     * The guide's shape as structure rather than prose: whether it opens and closes with a section
     * of its own, how many questions each research question's section holds, and the most minutes
     * it may ask of a participant. Versions before 4 hold none, and `intendedGuideShape` answers the
     * default for them.
     */
    guideShape: guideShapeSchema.optional(),
    /**
     * The audience size the type recommends and why, as the Audience step states it. Versions
     * before 5 hold none, and `intendedAudience` answers the default for them.
     */
    audience: audienceRecommendationSchema.optional(),
  })
  .readonly();

/**
 * Everything a Study Type tells the rest of the product about how to run a study.
 *
 * Every field but `materials` is a `string` or a `readonly string[]`, deliberately: a field is
 * shaped only once the consumer it is shaped for exists, and until then prose says enough. The
 * Materials step is the first such consumer, so its block is structure. A field is tightened by
 * appending a version that carries the new shape, never by editing one a study may be pinned to,
 * because a study stores a version number and never the content.
 */
export type StudyTypeTemplate = z.infer<typeof studyTypeTemplateSchema>;

/**
 * The template every Study Type runs on until one is written for it.
 *
 * It is the whole of what makes an unauthored type a working choice rather than a placeholder:
 * seeded with the type's name and its bring/get line, it says enough for the drafting agent to
 * run the study generically. Nothing marks such a study as lesser anywhere a researcher can see,
 * and nothing adds a caveat to what it produces.
 */
export const genericTemplate = (seed: { name: string; bringGet: string }): StudyTypeTemplate => ({
  version: 1,
  authored: false,
  intakeShape: `A ${seed.name} study. ${seed.bringGet} Ask what the researcher already has, take it in whatever form it arrives, and say back what was read before moving on.`,
  extractionSet: [
    'What the researcher is trying to decide, in their own words.',
    'What the study is about: the brand, product, campaign or audience at issue.',
    'Each material they brought, named and described in one line.',
    'What they already believe to be true, recorded as belief rather than as fact.',
    'What they have already settled, so the study does not spend questions re-asking it.',
  ],
  goalGuidance: `Offer three goals a ${seed.name} study could answer, each one a decision the researcher could act on afterwards. Recommend the one closest to what they said they need, and say for each what it will not settle.`,
  rqShape:
    'Four to six research questions, each answerable by what a participant can say out loud, and each naming the decision it informs. Drop any question the materials cannot support.',
  guideSkeleton: [
    'Open with one grounding question that gets participants describing the topic in their own words.',
    'Take the research questions in order, a section each.',
    'Put a poll where a count is worth more than a quote, and follow it with a question asking why.',
    'Show a stimulus once, in the section that needs it, and ask about it immediately.',
    'Close by asking what the study missed.',
  ],
  audienceGuidance:
    'Recommend the smallest audience that still reads cleanly for every group the goal names, and never below the quality floor. Screen on the behaviour the study is about rather than on stated interest.',
  briefShape: `A ${seed.name} brief leads with the verdict the goal asked for, then the evidence behind it, then what is not settled. Every claim names the research question it came from.`,
  methodText: `A ${seed.name} study, run as a live conversation with the recruited audience, with polls where a count is needed.`,
});

/**
 * The Materials block every Study Type's version 2 carries, keyed by the intake it asks for.
 *
 * These are placeholder policy values rather than authored content: the vocabulary is the
 * mockup's, one form type and one paired type exist so each intake can be shown, and every other
 * type takes a description or a pasted brief. The Category packs author each type's block by
 * appending a version over this one.
 */
const TEXT_MATERIALS: MaterialsShape = {
  intake: { kind: 'text' },
  facts: [
    { id: 'brand', label: 'Brand', required: true },
    { id: 'product', label: 'Product or subject', required: true },
    { id: 'audience', label: 'Audience mentioned', required: false },
    { id: 'competitors', label: 'Competitors named', required: false },
    { id: 'decision', label: 'The decision this informs', required: false },
  ],
};

/**
 * The form a brand-health study asks for.
 *
 * No field is required, so a form saved half-filled still settles and a required fact left empty
 * reaches the actionable request for it rather than a refused save.
 */
const FORM_MATERIALS: MaterialsShape = {
  intake: {
    kind: 'form',
    fields: [
      { id: 'brand', label: 'Your brand', fillsFact: 'brand', required: false },
      { id: 'category', label: 'Category, in a few words', fillsFact: 'category', required: false },
      {
        id: 'competitors',
        label: 'Competitors to include, up to five',
        fillsFact: 'competitors',
        maxItems: 5,
        required: false,
      },
      {
        id: 'anything_to_show',
        label: 'Anything to show people',
        fillsFact: 'anything_to_show',
        required: false,
      },
    ],
  },
  facts: [
    { id: 'brand', label: 'Brand', required: true },
    { id: 'category', label: 'Category', required: true },
    { id: 'competitors', label: 'Competitors', required: false },
    { id: 'anything_to_show', label: 'Anything to show people', required: false },
  ],
};

/** The two versions an ad or concept comparison asks for, and what differs between them. */
const PAIRED_MATERIALS: MaterialsShape = {
  intake: { kind: 'paired', labels: ['Version A', 'Version B'], differsFact: 'differs' },
  facts: [
    { id: 'brand', label: 'Brand', required: false },
    { id: 'compared', label: 'What is being compared', required: true },
    { id: 'differs', label: 'What differs', required: false },
  ],
};

const MATERIALS_BY_STUDY_TYPE: ReadonlyMap<StudyTypeId, MaterialsShape> = new Map<
  StudyTypeId,
  MaterialsShape
>([
  ['brand-health', FORM_MATERIALS],
  ['ad-comparison', PAIRED_MATERIALS],
]);

/**
 * Version 2 of a Study Type: its version 1 content unchanged, plus the Materials block.
 *
 * Built from the same generic seed rather than copied from the parsed version 1, so the two stay
 * independent values and the content pin holds each one separately.
 */
const withMaterials = (versionOne: StudyTypeTemplate, id: StudyTypeId): StudyTypeTemplate => ({
  ...versionOne,
  version: 2,
  materials: MATERIALS_BY_STUDY_TYPE.get(id) ?? TEXT_MATERIALS,
});

/**
 * How many research questions a study starts with when its template names no number. A placeholder
 * policy value, and the one every version 3 carries until each type's own count is authored.
 */
export const DEFAULT_RQ_COUNT = 3;

/** The research-question guidance version 3 carries: the shape each question takes, and no count. */
const RQ_SHAPE_WITHOUT_COUNT =
  'Each research question is answerable by what a participant can say out loud, and names the decision it informs. Drop any question the materials cannot support.';

/**
 * Version 3 of a Study Type: its version 2 content, with the research-question count as a number
 * and the count sentence taken out of the prose, so the two cannot disagree.
 */
const withRqCount = (versionTwo: StudyTypeTemplate): StudyTypeTemplate => ({
  ...versionTwo,
  version: 3,
  rqShape: RQ_SHAPE_WITHOUT_COUNT,
  rqCount: DEFAULT_RQ_COUNT,
});

/**
 * The guide shape every version 4 carries, and the one a version without its own is drafted to.
 * Every value is a placeholder policy value until each type's own shape is authored; 15 minutes is
 * where completion is said to hold up.
 */
export const DEFAULT_GUIDE_SHAPE: GuideShape = {
  opening: true,
  closing: true,
  questionsPerRq: { min: 1, max: 3 },
  maxMinutes: 15,
};

/** The guide skeleton version 4 carries: what the shape does not already say, as guidance. */
const GUIDE_SKELETON_WITHOUT_SHAPE = [
  'Put a poll where a count is worth more than a quote, and follow it with a question asking why.',
  'Show a stimulus once, in the section that needs it, and ask about it immediately.',
] as const;

/**
 * Version 4 of a Study Type: its version 3 content, with the guide's shape as structure and the
 * sentences that shape now states taken out of the prose, so the two cannot disagree.
 */
const withGuideShape = (versionThree: StudyTypeTemplate): StudyTypeTemplate => ({
  ...versionThree,
  version: 4,
  guideSkeleton: GUIDE_SKELETON_WITHOUT_SHAPE,
  guideShape: DEFAULT_GUIDE_SHAPE,
});

/**
 * The audience recommendation every version 5 carries, and the one a version without its own
 * answers. Placeholder policy values until each type's own is authored: never below the Quality
 * floor, and no more than a simulated run can field.
 */
export const DEFAULT_AUDIENCE_RECOMMENDATION: AudienceRecommendation = {
  recommendedCompletes: 25,
  rationale:
    'Enough people to see which answers are common and to hear a range of reasons. Choose more for a firmer read.',
};

/** Version 5 of a Study Type: its version 4 content, with the recommended audience as structure. */
const withAudienceRecommendation = (versionFour: StudyTypeTemplate): StudyTypeTemplate => ({
  ...versionFour,
  version: 5,
  audience: DEFAULT_AUDIENCE_RECOMMENDATION,
});

/** The audience size a study on this template version is recommended, and why. */
export const intendedAudience = (template: StudyTypeTemplate): AudienceRecommendation =>
  template.audience ?? DEFAULT_AUDIENCE_RECOMMENDATION;

/** The shape a guide on this template version is drafted to. */
export const intendedGuideShape = (template: StudyTypeTemplate): GuideShape =>
  template.guideShape ?? DEFAULT_GUIDE_SHAPE;

/** How many research questions a study on this template version starts with. */
export const intendedRqCount = (template: StudyTypeTemplate): number =>
  template.rqCount ?? DEFAULT_RQ_COUNT;

/**
 * The research-question guidance a drafter asked for exactly `intendedRqCount` questions is given.
 * A version with no `rqCount` states its count in prose, which can disagree with the default the
 * drafter is held to, so it is given the count-free shape instead: the number comes only from
 * `intendedRqCount`.
 */
export const rqShapeForDrafting = (template: StudyTypeTemplate): string =>
  template.rqCount === undefined ? RQ_SHAPE_WITHOUT_COUNT : template.rqShape;

/**
 * The steering prose an authored version must have written for its type, each read off a
 * template the way the prompts read it. `extractionSet` and `methodText` are not here: nothing
 * reads `extractionSet` on a version with a Materials block, and nothing reads `methodText` at
 * all, so an authored version takes both from the generic seed unchanged.
 */
const AUTHORED_PROSE_FIELDS = {
  intakeShape: (template: StudyTypeTemplate) => template.intakeShape,
  goalGuidance: (template: StudyTypeTemplate) => template.goalGuidance,
  rqShape: (template: StudyTypeTemplate) => template.rqShape,
  guideSkeleton: (template: StudyTypeTemplate) => template.guideSkeleton,
  audienceGuidance: (template: StudyTypeTemplate) => template.audienceGuidance,
  briefShape: (template: StudyTypeTemplate) => template.briefShape,
  'audience.rationale': (template: StudyTypeTemplate) => template.audience?.rationale,
} as const;

/**
 * A Brief shape asking the synthesis to quote or restate wording. The synthesis is told to do
 * neither — quotes are participants' cells shown by handle, and the research question, Goal, guide
 * and option wording are rendered by code — so a shape asking for it pulls against the rules it is
 * framed by.
 */
const QUOTE_OR_RESTATE =
  /\b(quot(?:e|es|ed|ing|ation|ations)|verbatim|restat\w*|word for word)\b/iu;

/** The Materials blocks every type shares until its own is written. */
const PLACEHOLDER_MATERIALS: readonly MaterialsShape[] = [
  TEXT_MATERIALS,
  FORM_MATERIALS,
  PAIRED_MATERIALS,
];

/**
 * How alike two pieces of wording may be before the second counts as a copy of the first: the
 * share of their distinct words they hold in common (Jaccard similarity), compared in lower case
 * with punctuation and spacing ignored. At this bar, changing one word of a generic paragraph still
 * leaves it a copy, while prose written for a type shares far fewer of its words. A conservative
 * bar: a short generic line with one word changed can fall under it.
 */
export const NEAR_COPY_SIMILARITY = 0.9;

/** Every string in a value, at any depth, in order: the words a block of structure says. */
const stringsIn = (value: unknown): string[] => {
  if (typeof value === 'string') {
    return [value];
  }
  if (value === null || typeof value !== 'object') {
    return [];
  }

  return Object.values(value).flatMap(stringsIn);
};

const wordsOf = (value: unknown): ReadonlySet<string> =>
  new Set(
    stringsIn(value)
      .join(' ')
      .toLowerCase()
      .split(/[^\p{L}\p{N}]+/u)
      .filter((word) => word !== ''),
  );

/**
 * How alike two values' wording is, from 0 to 1: the words they share over the words either holds,
 * read from every string in each (`NEAR_COPY_SIMILARITY` is the bar for a copy).
 */
export const wordingSimilarity = (left: unknown, right: unknown): number => {
  const a = wordsOf(left);
  const b = wordsOf(right);
  const shared = [...a].filter((word) => b.has(word)).length;
  const either = a.size + b.size - shared;

  return either === 0 ? 1 : shared / either;
};

/** Whether `value` is a copy of `original`, word for word or at most a word or two apart. */
const nearCopy = (value: unknown, original: unknown): boolean =>
  original !== undefined && wordingSimilarity(value, original) >= NEAR_COPY_SIMILARITY;

/**
 * Why an authored version is not authored, one sentence per reason, or none.
 *
 * Authored means written for the type: it carries every structured block, every field of its
 * steering prose says at least one word, none of that prose is a copy (`NEAR_COPY_SIMILARITY`) of
 * what one of the type's generic versions already says, and its Materials block is not a copy of
 * one of the shared placeholders. A version marked authored that copies the generic metadata, copies
 * it with a word changed, or leaves a field as punctuation or spacing alone, would otherwise count
 * as authored while saying nothing the generic template does not.
 */
const authoredVersionProblems = (
  template: StudyTypeTemplate,
  generic: readonly StudyTypeTemplate[],
): readonly string[] => {
  const problems: string[] = [];

  for (const block of ['materials', 'rqCount', 'guideShape', 'audience'] as const) {
    if (template[block] === undefined) {
      problems.push(`has no ${block}`);
    }
  }

  for (const [field, read] of Object.entries(AUTHORED_PROSE_FIELDS)) {
    const value = read(template);
    if (value === undefined) {
      continue;
    }

    if (wordsOf(value).size === 0) {
      problems.push(`has no ${field}`);
      continue;
    }
    if (generic.some((version) => nearCopy(value, read(version)))) {
      problems.push(`keeps the generic ${field}`);
    }
  }

  // An authored Brief shape reaches the synthesis as trusted instructions, and anything it asks for
  // must survive the gate the Brief's own words pass: a shape asking for "the top three" or "a
  // third" would have every Brief of the type refused as unreadable.
  if (!passesNumeralGate(template.briefShape)) {
    problems.push('has a briefShape the Brief’s numeral and quotation gate refuses');
  }
  if (QUOTE_OR_RESTATE.test(template.briefShape)) {
    problems.push('has a briefShape asking the Brief to quote or restate wording');
  }

  const { materials } = template;
  if (
    materials !== undefined &&
    PLACEHOLDER_MATERIALS.some((placeholder) => nearCopy(materials, placeholder))
  ) {
    problems.push('keeps a placeholder Materials block');
  }

  return problems;
};

/**
 * The registry's own shape: every one of the seventeen ids present, each holding at least one
 * version, numbered `1..n` in order with no gap and no repeat, and every version marked authored
 * written for its type (`authoredVersionProblems`). The registry is parsed as this module loads,
 * so a version that breaks either rule stops every process that imports the catalogue.
 *
 * Those are rules about numbering and about what authored means, and nothing more. They cannot see
 * a field edited on a version that kept its number, which is the other half of the append-only
 * requirement, and the half a study in progress is exposed to: `study-types.test.ts` pins every
 * held version's content by hash for exactly that, because no schema can tell content that changed
 * from content that was always this way.
 */
export const studyTypeTemplatesSchema = z
  .record(studyTypeIdSchema, z.array(studyTypeTemplateSchema).min(1))
  .superRefine((registry, context) => {
    for (const [id, versions] of Object.entries(registry)) {
      const generic = versions.filter((template) => !template.authored);

      versions.forEach((template, index) => {
        if (template.version !== index + 1) {
          context.addIssue({
            code: 'custom',
            message: `${id} holds version ${String(template.version)} at position ${String(index + 1)}`,
          });
        }

        if (template.authored) {
          for (const problem of authoredVersionProblems(template, generic)) {
            context.addIssue({
              code: 'custom',
              message: `${id} version ${String(template.version)} is marked authored but ${problem}`,
            });
          }
        }
      });
    }
  });

/**
 * One authored version as a Category module writes it: every steering field and every structured
 * block, written for the type. The registry adds the rest — `authored: true`, and `extractionSet`
 * and `methodText` from the type's generic seed, which nothing reads on a version like this.
 */
export type AuthoredTemplateEntry = Omit<
  StudyTypeTemplate,
  'authored' | 'extractionSet' | 'methodText' | 'materials' | 'rqCount' | 'guideShape' | 'audience'
> & {
  readonly materials: MaterialsShape;
  readonly rqCount: number;
  readonly guideShape: GuideShape;
  readonly audience: AudienceRecommendation;
};

/**
 * A Category module's authored versions, keyed by Study Type, each list in version order and
 * appended after the type's generic versions.
 */
export type AuthoredTemplates = Partial<Record<StudyTypeId, readonly AuthoredTemplateEntry[]>>;

/**
 * Every Category module's authored versions as one map, refusing a module that keys a Study Type of
 * another Category — or Custom, which has none — and a Study Type two modules both key. Each pack
 * writes its own Category's module, so either is a pack writing in the wrong place, and merging
 * past it would silently drop one module's versions.
 */
export const assembleAuthoredTemplates = (
  modules: readonly (readonly [CategoryId, AuthoredTemplates])[],
): AuthoredTemplates => {
  const assembled: AuthoredTemplates = {};
  for (const [categoryId, templates] of modules) {
    for (const [id, entries] of Object.entries(templates) as [
      StudyTypeId,
      AuthoredTemplateEntry[],
    ][]) {
      const owner = studyTypeById(id)?.categoryId;
      if (owner !== categoryId) {
        throw new Error(
          `the ${categoryId} templates module authors ${id}, which is ${owner === undefined ? 'in no Category' : `a ${owner} Study Type`}`,
        );
      }
      if (Object.hasOwn(assembled, id)) {
        throw new Error(`${id} is authored by two templates modules`);
      }
      assembled[id] = entries;
    }
  }

  return assembled;
};

/** Every Category module's authored versions, keyed by Study Type. */
const AUTHORED_TEMPLATES = assembleAuthoredTemplates([
  ['brand', BRAND_AUTHORED_TEMPLATES],
  ['creative', CREATIVE_AUTHORED_TEMPLATES],
  ['product', PRODUCT_AUTHORED_TEMPLATES],
  ['audience', AUDIENCE_AUTHORED_TEMPLATES],
]);

/**
 * What each Study Type's generic versions 1 to 5 were seeded with: its name and its bring/get line
 * as they read when those versions were written, the only words `genericTemplate` takes.
 *
 * The held versions are built from this map and never from the live catalogue entry, so a
 * catalogue row reworded for the picker card leaves every held version's content, and its hash, as
 * it was. A new Study Type adds its seed here as it is first written.
 */
export const GENERIC_TEMPLATE_SEEDS: Readonly<
  Record<StudyTypeId, { readonly name: string; readonly bringGet: string }>
> = {
  'brand-health': {
    name: 'Brand health',
    bringGet:
      'Bring your brand and up to five competitors. Get where you stand, and why people choose or skip you.',
  },
  'brand-comparison': {
    name: 'Brand comparison',
    bringGet: 'Bring the brands to compare. Get where you win and lose against each.',
  },
  'competitive-read': {
    name: 'Competitive read',
    bringGet:
      'Bring the alternatives people consider. Get how they see them and where you can win.',
  },
  'positioning-check': {
    name: 'Positioning check',
    bringGet: 'Bring the new positioning. Get whether it lands, and against whom.',
  },
  'naming-identity': {
    name: 'Naming and identity feedback',
    bringGet: 'Bring the name, tagline, logo, or pack. Get the reaction and the reasons.',
  },
  'campaign-validation': {
    name: 'Campaign validation',
    bringGet:
      'Bring the campaign creative and the message it should land. Get a verdict on whether it lands, with who, and what to fix.',
  },
  'ad-comparison': {
    name: 'Ad or concept comparison',
    bringGet: 'Bring two or more versions. Get a winner and the reasons.',
  },
  'message-testing': {
    name: 'Message testing',
    bringGet:
      'Bring the headlines, claims, or value props. Get which ones people repeat back and prefer.',
  },
  'creative-recall': {
    name: 'Creative recall and comprehension',
    bringGet: 'Bring the finished creative. Get what people remember and what confuses them.',
  },
  'concept-feedback': {
    name: 'Concept feedback',
    bringGet: 'Bring the concept. Get whether people want it, and what would make them.',
  },
  'feature-priorities': {
    name: 'Feature and experience priorities',
    bringGet:
      'Bring the product or program. Get what people would use, what breaks today, and what to build first.',
  },
  'offer-pricing': {
    name: 'Offer and pricing reaction',
    bringGet:
      'Bring the bundles, tiers, or price points. Get the stated reaction; not a pricing study.',
  },
  'audience-understanding': {
    name: 'Audience understanding',
    bringGet: 'Bring who you think they are. Get what they believe, want, and need to hear.',
  },
  'launch-planning': {
    name: 'Launch planning',
    bringGet: 'Bring the launch plan. Get the objections and missing proof points.',
  },
  'segment-check': {
    name: 'Segment or persona check',
    bringGet: 'Bring the target definition. Get whether it holds up and how two groups differ.',
  },
  'post-launch-read': {
    name: 'Post-launch or campaign read',
    bringGet: 'Bring what ran. Get what changed and what to fix.',
  },
  [CUSTOM_STUDY_TYPE_ID]: { name: CUSTOM_STUDY_TYPE_NAME, bringGet: CUSTOM_STUDY_TYPE_BRING_GET },
};

/** A Study Type's generic versions 1 to 5, each built from its seed and nothing else. */
const genericVersions = (
  seed: { name: string; bringGet: string },
  id: StudyTypeId,
): readonly StudyTypeTemplate[] => [
  genericTemplate(seed),
  withMaterials(genericTemplate(seed), id),
  withRqCount(withMaterials(genericTemplate(seed), id)),
  withGuideShape(withRqCount(withMaterials(genericTemplate(seed), id))),
  withAudienceRecommendation(withGuideShape(withRqCount(withMaterials(genericTemplate(seed), id)))),
];

/**
 * An authored entry as the version it is: the entry over the type's generic seed, which gives it
 * the `extractionSet` and `methodText` nothing reads on it, marked authored.
 */
const authoredVersion = (
  seed: { name: string; bringGet: string },
  entry: AuthoredTemplateEntry,
): StudyTypeTemplate => ({ ...genericTemplate(seed), ...entry, authored: true });

/** Every version a Study Type holds: its generic ones, then the ones written for it. */
const heldVersions = (
  seed: { name: string; bringGet: string },
  id: StudyTypeId,
): readonly StudyTypeTemplate[] => [
  ...genericVersions(seed, id),
  ...(AUTHORED_TEMPLATES[id] ?? []).map((entry) => authoredVersion(seed, entry)),
];

/**
 * Every template version every Study Type has ever had, oldest first.
 *
 * The registry is accretive: a new version is **appended** and no version already in it is ever
 * edited. That is what makes "a template edit does not change a study already in progress"
 * mechanical rather than a promise — a study stores the version it was created against and
 * resolves through that number forever, so nothing it reads is the latest of anything.
 *
 * Removing a version is therefore a data decision, not a code cleanup: every study pinned to it
 * becomes unresolvable, which `templateFor` reports by answering `undefined`.
 */
export const STUDY_TYPE_TEMPLATES: Record<StudyTypeId, readonly StudyTypeTemplate[]> = deepFreeze(
  studyTypeTemplatesSchema.parse(
    Object.fromEntries(
      STUDY_TYPE_IDS.map((id) => [id, heldVersions(GENERIC_TEMPLATE_SEEDS[id], id)]),
    ),
  ),
);

/**
 * The registry keyed for lookup, holding the seventeen ids and nothing else.
 *
 * A `Map` rather than the record itself, for the same reason the catalogue lookups use one. The
 * static type says every `StudyTypeId` is a key, and an id that came through `isStudyTypeId` is —
 * but an id read off a stored column reaches a lookup by assertion instead, so both an id the
 * registry stopped holding and a string like `toString` are reachable in a way the type cannot
 * see. Indexing an object answers the second with something off `Object.prototype`, which then
 * fails inside the lookup as though the registry were broken. A `Map` has no inherited keys, so
 * every id the registry does not hold gets the one answer a caller is told to expect.
 */
const TEMPLATES_BY_ID = new Map<StudyTypeId, readonly StudyTypeTemplate[]>(
  STUDY_TYPE_IDS.map((id) => [id, STUDY_TYPE_TEMPLATES[id]]),
);

/** The versions held for a Study Type, or `undefined` where the registry holds none. */
const templatesHeldFor = (id: StudyTypeId): readonly StudyTypeTemplate[] | undefined =>
  TEMPLATES_BY_ID.get(id);

/**
 * The exact version asked for, or `undefined` where the code no longer holds it.
 *
 * `undefined` means the study cannot be resolved and the caller must say so. Falling back to the
 * newest version would move a study under its owner silently, which is the single thing the
 * version number exists to prevent.
 */
export const templateFor = (id: StudyTypeId, version: number): StudyTypeTemplate | undefined =>
  templatesHeldFor(id)?.find((template) => template.version === version);

/**
 * The newest version in a list of them, or `undefined` where the list is empty.
 *
 * Expressed over a list rather than inside `currentTemplateVersion` so the rule can be held to a
 * list the registry does not hold, such as one a later version has been appended to.
 *
 * An empty list answers `undefined` rather than 0, because 0 is not a version any study could have
 * stored and is the one value a caller following the `undefined`-means-throw rule would take for
 * an answer.
 */
export const highestTemplateVersion = (
  templates: readonly StudyTypeTemplate[],
): number | undefined =>
  templates.reduce<number | undefined>(
    (highest, template) =>
      highest === undefined ? template.version : Math.max(highest, template.version),
    undefined,
  );

/**
 * The newest version held for a Study Type, which is what a fresh pick is stored against, or
 * `undefined` where the registry holds no version for that id at all.
 *
 * `undefined` is the same answer `templateFor` gives, for the same reason and with the same
 * obligation on the caller: the code cannot resolve this study and must say so rather than pick a
 * number. There is one rule about an id the registry does not hold, not two.
 *
 * Every Study Type holds its generic versions and then any written for it, so this is a type's
 * newest authored version where it has one and its newest generic one otherwise. Anything that
 * means "the version a study picked now gets" reads it here rather than naming a number, so
 * authoring a type moves it in one place; `highestTemplateVersion` is where the rule itself can be
 * held to lists of different lengths.
 */
export const currentTemplateVersion = (id: StudyTypeId): number | undefined => {
  const templates = templatesHeldFor(id);

  return templates === undefined ? undefined : highestTemplateVersion(templates);
};

/**
 * Whether a Study Type runs on a template written for it, and on which version: the status of its
 * current version, the one a study picked now is stored against.
 */
export type StudyTypeAuthoring =
  { status: 'authored'; version: number } | { status: 'generic'; version: number };

/**
 * The authoring status of the newest version in a list of them, or `undefined` where the list is
 * empty. Expressed over a list, as `highestTemplateVersion` is, so the rule can be held to a list
 * the registry does not hold.
 */
export const templateAuthoring = (
  templates: readonly StudyTypeTemplate[],
): StudyTypeAuthoring | undefined => {
  const version = highestTemplateVersion(templates);
  const template = templates.find((each) => each.version === version);

  if (version === undefined || template === undefined) {
    return undefined;
  }

  return { status: template.authored ? 'authored' : 'generic', version };
};

/**
 * A Study Type's authoring status, derived from its current version's `authored` flag and never
 * stored, or `undefined` where the registry holds no version for that id, as `templateFor` answers.
 *
 * This is the record of which types are authored. It is for the people building Sigora: nothing a
 * researcher sees says which kind of template a study runs on, and a study's own record is the
 * version it pinned, whose `authored` flag answers the same question for it.
 */
export const studyTypeAuthoring = (id: StudyTypeId): StudyTypeAuthoring | undefined => {
  const templates = templatesHeldFor(id);

  return templates === undefined ? undefined : templateAuthoring(templates);
};

/**
 * The Materials block of a template version.
 *
 * A version without the block has no Materials shape, and a study pinned to one is refused
 * rather than given another version's block: picking the type again pins the version that has one,
 * which is a choice made on a screen that says what was chosen rather than a silent change.
 */
export const materialsShapeFor = (template: StudyTypeTemplate): MaterialsShape => {
  if (template.materials === undefined) {
    throw new DomainError(
      'materials_shape_missing',
      'This study was set up before Sigora knew what to ask for here. Pick its type again to continue.',
      { template_version: template.version },
    );
  }

  return template.materials;
};

const describeIntake = (intake: MaterialsIntake): string => {
  switch (intake.kind) {
    case 'text': {
      return 'a description of the study in a sentence, or a pasted brief';
    }
    case 'form': {
      return `a form: ${intake.fields.map((field) => field.label).join('; ')}`;
    }
    case 'paired': {
      return `two versions side by side, "${intake.labels[0]}" and "${intake.labels[1]}", and the one thing that differs between them`;
    }
  }
};

/** The intake and the facts, from the Materials block where the version has one. */
const materialsGuidance = (template: StudyTypeTemplate): readonly string[] => {
  if (template.materials === undefined) {
    return [
      `Intake: ${template.intakeShape}`,
      `Facts to extract:\n${template.extractionSet.map((fact) => `- ${fact}`).join('\n')}`,
    ];
  }

  const { intake, facts } = template.materials;

  return [
    `Intake: ${template.intakeShape} On the Materials step the researcher gives ${describeIntake(intake)}.`,
    `Facts to extract:\n${facts.map((fact) => `- ${fact.label}${fact.required ? ' (required)' : ''}`).join('\n')}`,
  ];
};

/**
 * The guide's sections as prose lines, in order. A version with a guide shape states its structure
 * from the shape and the rest from the skeleton, so the prose and the structure cannot disagree.
 * The shape's most minutes are not among them: the Guide thread names that ceiling as Sigora's,
 * once, beside what each question costs.
 */
const guideGuidance = (template: StudyTypeTemplate): readonly string[] => {
  const shape = template.guideShape;
  if (shape === undefined) {
    return template.guideSkeleton;
  }

  const { min, max } = shape.questionsPerRq;
  const count = min === max ? String(min) : `${String(min)} to ${String(max)}`;

  return [
    ...(shape.opening
      ? [
          'Open with one grounding question that gets participants describing the topic in their own words.',
        ]
      : []),
    `Take the research questions in order, a section each, with ${count} ${max === 1 ? 'question' : 'questions'} in each.`,
    ...template.guideSkeleton,
    ...(shape.closing ? ['Close by asking what the study missed.'] : []),
  ];
};

/**
 * The goal guidance without its sentences about minutes: each authored type's ends by estimating
 * how long a study takes, which the Goal drafter reads to set each option's minutes and which the
 * conversation agent, told to work to the shape below it, would read as a limit on the guide.
 */
export const goalGuidanceWithoutMinutes = (goalGuidance: string): string =>
  goalGuidance
    .split(/(?<=\.)\s+/)
    .filter((sentence) => !/\bminutes?\b/i.test(sentence))
    .join(' ');

/**
 * The template as a prose block the conversation agent's prompt carries.
 *
 * `briefShape` and `methodText` are left out: they belong to the Brief, which is written by a
 * different agent from a different composer, and putting them in the drafting agent's prompt
 * would spend cache-stable prefix on instructions that agent cannot act on. No figure of minutes is
 * in it either; see `goalGuidanceWithoutMinutes` and `guideGuidance`.
 */
export const studyTypeGuidance = (template: StudyTypeTemplate): string =>
  [
    ...materialsGuidance(template),
    `Goal: ${goalGuidanceWithoutMinutes(template.goalGuidance)}`,
    `Research questions: ${template.rqShape}`,
    `Guide:\n${guideGuidance(template)
      .map((section) => `- ${section}`)
      .join('\n')}`,
    `Audience: ${template.audienceGuidance}`,
  ].join('\n\n');
